// src/utils/format.js
// Shared display formatting for fax numbers, dates and statuses. Times
// are in the person's time zone (My profile; see utils/timeZone.js).
import { timeZone, tzLabel } from './timeZone';

export const STATUS_COLORS = {
  delivered: { bg: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' },
  received:  { bg: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' },
  failed:    { bg: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)' },
  queued:    { bg: 'var(--c-e3f2fd)', color: 'var(--t-1565c0)' },
  requeued:  { bg: 'var(--c-fff8e1)', color: 'var(--t-e65100)' },
  'user resent': { bg: 'var(--c-f3e5f5)', color: 'var(--t-6a1b9a)' },   // 3.64.4
};
// 3.64.4 (owner): a sent fax's badge: "user resent" when its retry wasn't
// needed because the sender sent the same document to the number again
export const outStatus = f => (f.requeued ? (f.requeuedBy === 'superseded-by-manual-send' ? 'user resent' : 'requeued') : f.status);

export function formatFax(num) {
  if (!num) return '—';
  const n = String(num).replace(/\D/g, '');
  if (n.length === 11) return `+${n[0]} (${n.slice(1,4)}) ${n.slice(4,7)}-${n.slice(7)}`;
  if (n.length === 10) return `+1 (${n.slice(0,3)}) ${n.slice(3,6)}-${n.slice(6)}`;
  return num;
}

export function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: timeZone(), month: 'short', day: 'numeric',
    year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) + ' ' + tzLabel(new Date(iso));
}

// Short date for the fax lists (3.25.0): 10/5/26, 9:05 AM ET (no leading
// zeros, two-digit year) so the rows fit
export function formatShortDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: timeZone(), month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit'
  }) + ' ' + tzLabel(new Date(iso));
}

// 3.51.0: phone cards: today → "9:28 AM", this year → "Oct 10", older → "Oct 10, 2025"
// (in the person's time zone); the full date and time are in the fax's details
export function formatCardDate(iso) {
  if (!iso) return '—';
  const tz = timeZone(), d = new Date(iso);
  const day = x => x.toLocaleDateString('en-CA', { timeZone: tz });
  if (day(d) === day(new Date())) return d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
  const sameYear = d.toLocaleString('en-US', { timeZone: tz, year: 'numeric' }) === new Date().toLocaleString('en-US', { timeZone: tz, year: 'numeric' });
  return d.toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
}

export function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-US', {
    timeZone: timeZone(), hour: 'numeric', minute: '2-digit'
  }) + ' ' + tzLabel(new Date(iso));
}

// Automatic retries are sent by a checker that runs every 5 minutes, so a
// retry due at 5:36 goes out between 5:36 and 5:41. Show that window.
export const RETRY_CHECK_MINUTES = 5;
function timeParts(date) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: timeZone(), hour: 'numeric', minute: '2-digit' }).formatToParts(date);
  const get   = type => parts.find(p => p.type === type)?.value || '';
  return { time: `${get('hour')}:${get('minute')}`, period: get('dayPeriod') };
}
// 3.78.0: the same, with the day in front when it isn't today ("Tue 8:05–8:10 AM ET")
export function formatRetryWindowDay(iso) {
  if (!iso) return '—';
  const tz = timeZone(), d = new Date(iso);
  const day = x => x.toLocaleDateString('en-CA', { timeZone: tz });
  return `${day(d) === day(new Date()) ? '' : `${d.toLocaleDateString('en-US', { timeZone: tz, weekday: 'short' })} `}${formatRetryWindow(iso)}`;
}
export function formatRetryWindow(iso) {
  if (!iso) return '—';
  const start = new Date(iso);
  const end   = new Date(start.getTime() + RETRY_CHECK_MINUTES * 60000);
  const a = timeParts(start), b = timeParts(end);
  return a.period === b.period
    ? `${a.time}–${b.time} ${b.period} ${tzLabel(end)}`
    : `${a.time} ${a.period}–${b.time} ${b.period} ${tzLabel(end)}`;
}
